// DV360 launcher-result parsing helpers. All pure — no React, no fetches.
// These understand the canonical DV360 insertionOrders[] / lineItems[] /
// packages[] shape produced by the campaign-services launcher, and tolerate
// the MMGY-processed variants where some of those fields are nested under
// extra envelopes.

// Parse the IO displayName for human-readable parts. Real example:
//   "IOID#24541:PKID#77422:LAH Transient FY25_Display_Prospecting($7.5)"
// → { ioId:'24541', pkId:'77422', packageName:'LAH Transient FY25',
//     formatLabel:'Display', categoryLabel:'Prospecting', rate:7.5 }
export function parseIoDisplayName(name = '') {
  const out = { ioId: null, pkId: null, packageName: null, formatLabel: null, categoryLabel: null, rate: null };
  const ioMatch = name.match(/IOID#(\d+)/);
  if (ioMatch) out.ioId = ioMatch[1];
  const pkMatch = name.match(/PKID#(\d+)/);
  if (pkMatch) out.pkId = pkMatch[1];
  const rateMatch = name.match(/\(\$?([\d.]+)\)/);
  if (rateMatch) out.rate = Number(rateMatch[1]);
  // Tail after the second colon is "PackageName_Format_Category(...)"
  const tail = name.replace(/^IOID#\d+:?/, '').replace(/^PKID#\d+:?/, '').replace(/\([^)]*\)/, '').trim();
  if (tail) {
    const parts = tail.split('_').map((p) => p.trim()).filter(Boolean);
    if (parts.length >= 3) {
      out.packageName = parts.slice(0, parts.length - 2).join(' ');
      out.formatLabel = parts[parts.length - 2];
      out.categoryLabel = parts[parts.length - 1];
    } else if (parts.length === 2) {
      out.packageName = parts[0];
      out.formatLabel = parts[1];
    } else if (parts.length === 1) {
      out.packageName = parts[0];
    }
  }
  return out;
}

// True when this IO's budget is denominated in IMPRESSIONS rather than currency.
//
// DV360 has no separate impression-amount field on a budget segment: an
// impression-goal IO keeps its impression COUNT in `budgetAmountMicros`,
// un-scaled, and says so through `budget.budgetUnit`. Real capture
// (test_campaign_copy_handler.py:504-509): budgetUnit BUDGET_UNIT_IMPRESSIONS,
// budgetAmountMicros "212717", and pacing.dailyMaxImpressions 6445 over a
// 33-day flight ≈ 212,685 — a count, to within a rounding day.
//
// Our own launcher overloads the field the same way on the way out:
// dv360_campaign_preprocessor.py:270-299 writes `budgetAmountInCurrency × 1e6`
// and `int(budgetAmountInImpressions)` into that one key. So the unit is not
// decoration — nothing may read `budgetAmountMicros` as money without it.
export function isImpressionBudget(io) {
  const unit = io?.budget?.budgetUnit ?? io?.budget?.budgetUnitType ?? '';
  return String(unit).toUpperCase().includes('IMPRESSION');
}

// Compute the IO's total impression units. The DV360 launcher writes
// `budgetAmountMicros: 0` and a `budgetAmountPercentage` per segment when
// the source IO is impression-based — the real total lives in `packages[]`
// under matching package_key (or by IO displayName / packageKey).
export function getIoImpressions(io, pkgByKey, pkgByIoMatch) {
  // 1. If a budgetSegment has a real micros/imps value, use the sum.
  //    `budgetAmountInImpressions` is the launch TEMPLATE's key; an IO read
  //    back from the DV360 API (import, copy) keeps the same count in
  //    `budgetAmountMicros` instead. Reading only the template key is why
  //    every imported impressions campaign showed 0 imps.
  const impressionUnit = isImpressionBudget(io);
  const segs = io?.budget?.budgetSegments || [];
  let segTotal = 0;
  let segHadAny = false;
  for (const seg of segs) {
    const amt = Number(seg.budgetAmountInImpressions ?? seg.budgetAmountImpressions
      ?? (impressionUnit ? seg.budgetAmountMicros : undefined) ?? 0);
    if (amt > 0) { segTotal += amt; segHadAny = true; }
  }
  if (segHadAny) return segTotal;

  // 2. Match against packages[] by packageKey, then by IO displayName fields.
  const direct = io?.packageKey && pkgByKey.get(io.packageKey);
  if (direct?.units) return Number(direct.units);
  const meta = parseIoDisplayName(io?.displayName || '');
  const byPkId = meta.pkId && pkgByIoMatch.get(`pk:${meta.pkId}`);
  if (byPkId?.units) return Number(byPkId.units);
  const byName = meta.packageName && pkgByIoMatch.get(`name:${meta.packageName.toLowerCase()}`);
  if (byName?.units) return Number(byName.units);

  // 3. Fallback: zero so the row still renders.
  return 0;
}

// Build an aggregate package lookup. Packages can be looked up by:
//   - their own package_key (canonical)
//   - the IO's packageKey when it matches
//   - the PKID# parsed from the IO displayName
//   - the package's `name` lowercased
export function buildPackageIndex(packages = []) {
  const pkgByKey = new Map();
  const pkgByIoMatch = new Map();
  for (const p of packages) {
    if (p.package_key) pkgByKey.set(p.package_key, p);
    if (p.name) pkgByIoMatch.set(`name:${String(p.name).toLowerCase()}`, p);
    if (p.package_key) pkgByIoMatch.set(`pk:${p.package_key.replace(/[^0-9]/g, '')}`, p);
  }
  return { pkgByKey, pkgByIoMatch };
}

// Sum all package budgets (currency, USD).
export function sumPackageBudget(packages = []) {
  return (packages || []).reduce((s, p) => s + (Number(p.budget) || 0), 0);
}

// Sum all package units (impressions).
export function sumPackageUnits(packages = []) {
  return (packages || []).reduce((s, p) => s + (Number(p.units) || 0), 0);
}
